import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import TextField from '@mui/material/TextField';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast, ToastContainer } from 'react-toastify';

const Experience = ({ userId, hasExperienceChanged, setHasExperienceChanged }) => {
    const BASE_API_URL = process.env.REACT_APP_API_URL;

    const topRef = useRef(null); // <-- REF for scrolling to top

    const [isExperience, setIsExperience] = useState(false);
    const [experienceList, setExperienceList] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [disableFresher, setDisableFresher] = useState(false);
    const [generatedOtp, setGeneratedOtp] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [enterOtpValue, setEnterOtpValue] = useState('');
    const [otpVerified, setOtpVerified] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [checkboxDisable, setCheckboxDisable] = useState(false);
    const [hasVerifiedExperience, setHasVerifiedExperience] = useState(false);
    const [refreshCounter, setRefreshCounter] = useState(0);

    const publicDomains = [/* your domain list */];

    const [newExperience, setNewExperience] = useState({
        isExperience: true,
        yearsOfExperience: 0,
        lastCompany: '',
        jobRole: '',
        officialEmailId: '',
        isCurrentlyWorking: false,
    });

    useEffect(() => {
        const fetchInitialExperience = async () => {
            try {
                const response = await axios.get(`${BASE_API_URL}/getExperiencesByUserId`, {
                    params: { userId }
                });

                const experiences = response.data;

                if (Array.isArray(experiences) && experiences.length > 0) {
                    setExperienceList(experiences);
                    setIsExperience(true);
                    setDisableFresher(true);

                    const isAnyCurrentlyWorking = experiences.some(
                        exp => exp.isCurrentlyWorking === true
                    );

                    setCheckboxDisable(isAnyCurrentlyWorking);
                    setHasVerifiedExperience(isAnyCurrentlyWorking);
                } else {
                    setExperienceList([]);
                    setIsExperience(false);
                    setDisableFresher(false);
                    setCheckboxDisable(false);
                    setHasVerifiedExperience(false);
                }

            } catch (error) {
                console.error('Error fetching experiences on mount:', error);
                setErrorMessage('Error fetching experiences');
            }
        };

        fetchInitialExperience();
    }, [userId, refreshCounter]);

    const handleExperienceRadioChange = (e) => {
        const isSelected = e.target.value === 'experience';
        setIsExperience(isSelected);

        if (isSelected) {
            fetchExperiences();
        } else {
            setExperienceList([]);
            setShowForm(false);
        }
    };

    const fetchExperiences = async () => {
        try {
            const response = await axios.get(`${BASE_API_URL}/getExperiencesByUserId`, {
                params: { userId }
            });

            setExperienceList(response.data);
        } catch (error) {
            console.error('Error fetching experiences:', error);
            setErrorMessage('Error fetching experiences');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'officialEmailId') {
            const emailDomain = value.split('@')[1];

            if (emailDomain && publicDomains.includes(emailDomain.toLowerCase())) {
                setEmailError(
                    'Public domains are not allowed. Please enter a valid company official email ID.'
                );
                setCheckboxDisable(true);
            } else {
                setEmailError('');
                setCheckboxDisable(false);
            }
        }

        setNewExperience(prev => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = async (e) => {
        const isChecked = e.target.checked;

        setNewExperience(prev => ({
            ...prev,
            isCurrentlyWorking: isChecked
        }));

        if (!isChecked) {
            setOtpVerified(false);
            setGeneratedOtp('');
            setShowOtpModal(false);
            return;
        }

        if (isChecked && newExperience.officialEmailId) {
            try {
                await sendOtpToEmail(newExperience.officialEmailId);
                toast.success(
                    "Verification code sent successfully to your official email!"
                );
            } catch (err) {
                console.error("OTP sending failed", err);
            }
        }
    };

    const sendOtpToEmail = async (email) => {
        try {
            const response = await axios.get(`${BASE_API_URL}/sendOtpToEmail`, {
                params: { email }
            });

            setGeneratedOtp(response.data);
            setShowOtpModal(true);

        } catch (error) {
            console.error("Failed to send Verification code:", error);

            setErrorMessage(
                "Failed to send Verification code to official email."
            );

            toast.error(
                "Failed to send Verification code. Please try again."
            );
        }
    };

    const handleOtpSubmit = () => {
        if (enterOtpValue == generatedOtp) {
            setOtpVerified(true);
            setShowOtpModal(false);
            setErrorMessage('');
            setEnterOtpValue('');
        } else {
            setErrorMessage(
                'Invalid Verification code. Please try again.'
            );
        }
    };

    const handleAddExperience = async () => {
        if (
            !newExperience.yearsOfExperience ||
            !newExperience.lastCompany ||
            !newExperience.jobRole ||
            !newExperience.officialEmailId
        ) {
            toast.error('Please fill in all required fields.');
            return;
        }

        try {
            const updatedExperience = {
                ...newExperience,
                isExperience: true,
                isCurrentlyWorking: otpVerified,
            };

            await axios.put(
                `${BASE_API_URL}/setNewExperiencesByUserId`,
                updatedExperience,
                {
                    params: { userId },
                }
            );

            setExperienceList(prevList => [
                ...prevList,
                updatedExperience
            ]);

            if (otpVerified) {
                setHasVerifiedExperience(true);
                setCheckboxDisable(true);
            }

            setNewExperience({
                isExperience: true,
                yearsOfExperience: 0,
                lastCompany: '',
                jobRole: '',
                officialEmailId: '',
                isCurrentlyWorking: false,
            });

            setOtpVerified(false);
            setShowForm(false);
            setHasExperienceChanged(true);
            setRefreshCounter(prev => prev + 1);

            // Scroll to top
            topRef.current?.scrollIntoView({
                behavior: 'smooth'
            });

        } catch (error) {
            console.error('Error saving new experience:', error);
            setErrorMessage('Failed to save new experience');

        } finally {
            setHasExperienceChanged(false);
        }
    };

    const handleRemoveExperience = async (indexToRemove) => {
        try {
            const response = await axios.put(
                `${BASE_API_URL}/deleteExperienceByUserId`,
                null,
                {
                    params: {
                        userId,
                        indexToRemove,
                    },
                }
            );

            const updatedList = response.data;

            setExperienceList(updatedList);
            setHasExperienceChanged(true);
            setRefreshCounter(prev => prev + 1);

            if (updatedList.length === 0) {
                setDisableFresher(false);
                setIsExperience(false);
            }

            const isAnyCurrentlyWorking = updatedList.some(
                exp => exp.isCurrentlyWorking === true
            );

            setCheckboxDisable(isAnyCurrentlyWorking);
            setHasVerifiedExperience(isAnyCurrentlyWorking);

            // Scroll to top
            topRef.current?.scrollIntoView({
                behavior: 'smooth'
            });

        } catch (error) {
            console.error('Error deleting experience:', error);
            setErrorMessage('Failed to delete experience');

        } finally {
            setHasExperienceChanged(false);
        }
    };

    return (
        <>
            {/* Scroll target */}
            <div ref={topRef}></div>

            <div className="candidate-profile-details">

                {/* Header */}
                <div
                    className="d-flex justify-content-between align-items-center mb-4"
                    style={{
                        borderBottom: '1px solid #e5e7eb',
                        paddingBottom: '12px'
                    }}
                >
                    <div>
                        <h4 className="mb-1">
                            Experience Details
                        </h4>

                        <p className="text-danger mb-0">
                            Note: The following information is not required for freshers.
                        </p>
                    </div>

                    {isExperience && !showForm && (
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setShowForm(true)}
                            style={{
                                borderRadius: '8px',
                                padding: '8px 16px',
                                fontWeight: '500'
                            }}
                        >
                            + Add Experience
                        </button>
                    )}
                </div>

                {/* Experience / Fresher */}
                <div className="mb-4">

                    <div className="form-check form-check-inline">
                        <input
                            className="form-check-input"
                            type="radio"
                            id="fresher"
                            name="experienceStatus"
                            value="fresher"
                            checked={!isExperience}
                            disabled={disableFresher}
                            onChange={handleExperienceRadioChange}
                        />

                        <label
                            className="form-check-label"
                            htmlFor="fresher"
                        >
                            Fresher
                        </label>
                    </div>

                    <div className="form-check form-check-inline">
                        <input
                            className="form-check-input"
                            type="radio"
                            id="experience"
                            name="experienceStatus"
                            value="experience"
                            checked={isExperience}
                            onChange={handleExperienceRadioChange}
                        />

                        <label
                            className="form-check-label"
                            htmlFor="experience"
                        >
                            Experience
                        </label>
                    </div>

                </div>

                {/* Error Message */}
                {errorMessage && (
                    <div
                        className="alert alert-danger"
                        role="alert"
                    >
                        {errorMessage}
                    </div>
                )}

                {/* Experience List */}
                {isExperience && experienceList.length > 0 && (
                    <div className="existing-education mb-4">

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h5 className="mb-1">
                                    Work Experience
                                </h5>

                                <span className="text-muted">
                                    Total Years of Experience:{' '}
                                    <strong>
                                        {experienceList.reduce(
                                            (total, exp) =>
                                                total +
                                                Number(exp.yearsOfExperience),
                                            0
                                        )}
                                    </strong>
                                </span>
                            </div>
                        </div>

                        <div className="row">

                            {experienceList.map((exp, index) => (

                                <div
                                    className="col-md-6 mb-4"
                                    key={index}
                                >

                                    <div
                                        className="card h-100 shadow-sm"
                                        style={{
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '12px',
                                            overflow: 'hidden'
                                        }}
                                    >

                                        <div
                                            className="card-body"
                                            style={{
                                                padding: '20px',
                                                position: 'relative'
                                            }}
                                        >

                                            {/* Remove Button */}
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() =>
                                                    handleRemoveExperience(index)
                                                }
                                                style={{
                                                    position: 'absolute',
                                                    top: '15px',
                                                    right: '15px',
                                                    borderRadius: '6px',
                                                    padding: '5px 10px'
                                                }}
                                                title="Remove experience"
                                            >
                                                Remove
                                            </button>

                                            {/* Company */}
                                            <h5
                                                className="card-title mb-2"
                                                style={{
                                                    paddingRight: '90px',
                                                    fontWeight: '600'
                                                }}
                                            >
                                                {exp.lastCompany}
                                            </h5>

                                            {/* Job Role */}
                                            <h6 className="card-subtitle mb-3 text-muted">
                                                {exp.jobRole}
                                            </h6>

                                            <div className="card-text">

                                                <div className="mb-2">
                                                    <strong>
                                                        Years of Experience:
                                                    </strong>
                                                    <br />
                                                    <span>
                                                        {exp.yearsOfExperience}
                                                    </span>
                                                </div>

                                                <div className="mb-2">
                                                    <strong>
                                                        Official Email ID:
                                                    </strong>
                                                    <br />

                                                    <span>
                                                        {exp.officialEmailId}

                                                        {exp.isCurrentlyWorking && (
                                                            <FontAwesomeIcon
                                                                icon={faCheckCircle}
                                                                style={{
                                                                    color: 'green',
                                                                    marginLeft: '8px'
                                                                }}
                                                                title="Verified"
                                                            />
                                                        )}
                                                    </span>
                                                </div>

                                                {exp.isCurrentlyWorking && (
                                                    <div className="mt-2">
                                                        <strong className="text-success">
                                                            Status:
                                                        </strong>
                                                        <br />

                                                        <span className="text-success">
                                                            Currently working in this company
                                                        </span>
                                                    </div>
                                                )}

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            ))}

                        </div>

                    </div>
                )}

                {/* Add Experience Form */}
                {isExperience && showForm && (
                    <div
                        className="candidate-profile-details"
                        style={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '20px',
                            background: '#fff'
                        }}
                    >

                        {/* Form Header */}
                        <div
                            className="d-flex justify-content-between align-items-center mb-4"
                        >
                            <h5 className="mb-0">
                                Add Experience
                            </h5>

                            {experienceList.length > 0 && (
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm"
                                    onClick={() => {
                                        setShowForm(false);
                                        setErrorMessage('');
                                    }}
                                    style={{
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px'
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleAddExperience();
                            }}
                        >

                            <div className="container-fluid">

                                {/* Row 1 */}
                                <div className="row mb-3">

                                    <div className="col-md-6 mb-3 mb-md-0">

                                        <div className="form-group">

                                            <label
                                                htmlFor="yearsOfExperience"
                                                className="required-label mb-2"
                                            >
                                                <b>
                                                    Years of Experience:
                                                </b>
                                            </label>

                                            <input
                                                type="text"
                                                id="yearsOfExperience"
                                                className="form-control"
                                                name="yearsOfExperience"
                                                value={newExperience.yearsOfExperience}
                                                onChange={handleInputChange}
                                                placeholder="Enter years of experience"
                                                required
                                            />

                                        </div>

                                    </div>

                                    <div className="col-md-6">

                                        <div className="form-group">

                                            <label
                                                htmlFor="lastCompany"
                                                className="required-label mb-2"
                                            >
                                                <b>
                                                    Last Company:
                                                </b>
                                            </label>

                                            <input
                                                type="text"
                                                id="lastCompany"
                                                className="form-control"
                                                name="lastCompany"
                                                value={newExperience.lastCompany}
                                                onChange={handleInputChange}
                                                placeholder="Enter last company name"
                                                required
                                            />

                                        </div>

                                    </div>

                                </div>

                                {/* Row 2 */}
                                <div className="row mb-3">

                                    <div className="col-md-6 mb-3 mb-md-0">

                                        <div className="form-group">

                                            <label
                                                htmlFor="jobRole"
                                                className="required-label mb-2"
                                            >
                                                <b>
                                                    Job Role:
                                                </b>
                                            </label>

                                            <input
                                                type="text"
                                                id="jobRole"
                                                className="form-control"
                                                name="jobRole"
                                                value={newExperience.jobRole}
                                                onChange={handleInputChange}
                                                placeholder="Enter your job role"
                                                required
                                            />

                                        </div>

                                    </div>

                                    <div className="col-md-6">

                                        <div className="form-group">

                                            <label
                                                htmlFor="officialEmailId"
                                                className="required-label mb-2"
                                            >
                                                <b>
                                                    Official Email ID:
                                                </b>
                                            </label>

                                            <input
                                                type="email"
                                                id="officialEmailId"
                                                className="form-control"
                                                name="officialEmailId"
                                                value={newExperience.officialEmailId}
                                                onChange={handleInputChange}
                                                placeholder="Enter company official email"
                                                required
                                            />

                                            {emailError && (
                                                <div className="text-danger mt-1">
                                                    {emailError}
                                                </div>
                                            )}

                                        </div>

                                    </div>

                                </div>

                                {/* Currently Working */}
                                <div className="form-check mt-3">

                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="currentlyWorking"
                                        checked={newExperience.isCurrentlyWorking}
                                        onChange={handleCheckboxChange}
                                        disabled={
                                            checkboxDisable ||
                                            hasVerifiedExperience
                                        }
                                    />

                                    <label
                                        className="form-check-label"
                                        htmlFor="currentlyWorking"
                                    >
                                        I'm currently working in this company
                                    </label>

                                </div>

                                {/* Save Button */}
                                <div className="d-flex justify-content-end mt-4">

                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={
                                            (!checkboxDisable &&
                                                newExperience.isCurrentlyWorking &&
                                                !otpVerified)
                                        }
                                        style={{
                                            borderRadius: '8px',
                                            padding: '9px 20px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Save Experience
                                    </button>

                                </div>

                            </div>

                        </form>

                    </div>
                )}

                {/* OTP Modal */}
                <Modal
                    show={showOtpModal}
                    onHide={() => setShowOtpModal(false)}
                >

                    <Modal.Header closeButton>
                        <Modal.Title>
                            OTP Verification
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>

                        <p>
                            A Verification code has been sent to your email.
                            Please enter it below:
                        </p>

                        <TextField
                            fullWidth
                            type="text"
                            name="otp"
                            placeholder="Enter Code"
                            value={enterOtpValue}
                            onChange={(e) =>
                                setEnterOtpValue(e.target.value)
                            }
                        />

                        {errorMessage && (
                            <div className="text-danger mt-2">
                                {errorMessage}
                            </div>
                        )}

                    </Modal.Body>

                    <Modal.Footer>

                        <Button
                            variant="secondary"
                            onClick={() =>
                                setShowOtpModal(false)
                            }
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="primary"
                            onClick={handleOtpSubmit}
                        >
                            Verify Code
                        </Button>

                    </Modal.Footer>

                </Modal>

                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                />

            </div>
        </>
    );
};

export default Experience;